import React, { ReactNode } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

type GradientColor = 'blue' | 'purple' | 'green' | 'red' | 'orange' | 'cyan' | 'pink' | 'indigo';

interface CustomModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl';
  gradient?: GradientColor;
  headerContent?: ReactNode; // Extra tartalom a fejlécben (pl. státusz, prioritás)
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
};

const gradientClasses = {
  blue: 'bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-700 dark:via-indigo-700 dark:to-purple-700',
  purple: 'bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 dark:from-purple-700 dark:via-pink-700 dark:to-red-700',
  green: 'bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 dark:from-green-700 dark:via-emerald-700 dark:to-teal-700',
  red: 'bg-gradient-to-r from-red-600 via-orange-600 to-yellow-600 dark:from-red-700 dark:via-orange-700 dark:to-yellow-700',
  orange: 'bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 dark:from-orange-700 dark:via-amber-700 dark:to-yellow-700',
  cyan: 'bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 dark:from-cyan-700 dark:via-blue-700 dark:to-indigo-700',
  pink: 'bg-gradient-to-r from-pink-600 via-rose-600 to-red-600 dark:from-pink-700 dark:via-rose-700 dark:to-red-700',
  indigo: 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 dark:from-indigo-700 dark:via-purple-700 dark:to-pink-700',
};

export default function CustomModal({
  isOpen,
  onClose,
  title,
  subtitle,
  icon: Icon,
  children,
  footer,
  size = 'xl',
  gradient = 'blue',
  headerContent
}: CustomModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 transition-opacity bg-black bg-opacity-50" 
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Modal */}
        <div className={`inline-block w-full ${sizeClasses[size]} my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-2xl rounded-xl`}>
          {/* Gradient Header */}
          <div className={`${gradientClasses[gradient]} p-6 rounded-t-xl`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                {Icon && (
                  <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                )}
                <div>
                  <h3 className="text-2xl font-bold text-white mb-1">
                    {title}
                  </h3>
                  {subtitle && (
                    <p className="text-sm text-white/80">
                      {subtitle}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {headerContent && (
                  <div className="hidden sm:block">
                    {headerContent}
                  </div>
                )}
                <button
                  onClick={onClose}
                  className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-all"
                  aria-label="Close"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="px-6 pb-6 pt-2 border-t border-gray-200 dark:border-gray-700">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
